import React, { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Building2 } from 'lucide-react';
import { SpendTrendChart } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import { fmtMoney, accountKey, rowSpend, rowSpendText } from './accountsFormat';
import './AccountsCompetitiveTab.css';

/*
 * AccountsCompetitiveTab — competitor footprint for the agency (P5b port
 * of the GAS CompetitivePanel anatomy): share stat row, 5-yr spend trend
 * (shared SpendTrendChart), and our-vs-competitor vendor lists with
 * share bars. Clicking a vendor drills into its transactions via the
 * shared drilldown modal (sankey vendor drilldown scoped to this dept).
 * Data: GET /api/v1/competitive/footprint?account=&years=3 (main) +
 * GET /api/v1/accounts/:account/evidence (spendTrend) +
 * GET /api/v1/procurement/summary?account= (spend by category).
 *
 * All three were API-key-only until the parity work: TekStak's apiClient sends
 * only a JWT, so the footprint — this tab's PRIMARY query — answered 401 and
 * the tab rendered empty. They now go through publicAgencyRead().
 */

const FOOTPRINT_YEARS = 3;

/** GAS's "Spend by Category" card. Shares the bar vocabulary with the
 *  footprint lists below so the tab reads as one thing, not three. */
function CategoryRows({ categories }) {
  const max = Math.max(...categories.map(rowSpend), 1);
  const total = categories.reduce((s, c) => s + rowSpend(c), 0);
  return (
    <div className="cmp-bars">
      {categories.map((c) => {
        const label = c.category || c.name;
        const value = rowSpend(c);
        return (
          <div className="cmp-bar-row cmp-bar-row--static" key={label}>
            <span className="cmp-bar-label">{label}</span>
            <span className="cmp-bar-track">
              <span className="cmp-bar-fill" style={{ width: `${(value / max) * 100}%` }} />
            </span>
            <span className="cmp-bar-value">
              {rowSpendText(c)}
              {total > 0 && <span className="cmp-bar-share"> · {((value / total) * 100).toFixed(1)}%</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function VendorRows({ vendors, total, ours, onDrill }) {
  const max = Math.max(...vendors.map((v) => v.spend), 1);
  return (
    <div className="cmp-bars">
      {vendors.map((v) => (
        <button
          type="button"
          className="cmp-bar-row"
          key={v.vendor}
          onClick={() => onDrill(v.vendor)}
          title={`${v.vendor} — ${v.poCount} POs · ${(v.categories || []).slice(0, 4).join(', ') || 'uncategorized'} · click for transactions`}
        >
          <span className="cmp-bar-label">{v.vendor}</span>
          <span className="cmp-bar-track">
            <span
              className={`cmp-bar-fill${ours ? ' cmp-bar-fill--ours' : ''}`}
              style={{ width: `${(v.spend / max) * 100}%` }}
            />
          </span>
          <span className="cmp-bar-value">
            {v.spendFormatted || fmtMoney(v.spend)}
            {total > 0 && <span className="cmp-bar-share"> · {((v.spend / total) * 100).toFixed(1)}%</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function AccountsCompetitiveTab({ selectedDepartment }) {
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const [drillVendor, setDrillVendor] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-competitive-footprint', account],
    queryFn: () => apiClient.get('/api/v1/competitive/footprint', {
      params: { account, years: FOOTPRINT_YEARS },
    }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  // Spend trend is a separate, best-effort read — the footprint stays
  // useful without it, so its failure shows an inline note, not a wall.
  const trendQuery = useQuery({
    queryKey: ['accounts-evidence', account],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/evidence`),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  // Spend by category — the one GAS Competitive card TekStak was missing.
  const categoryQuery = useQuery({
    queryKey: ['accounts-procurement-summary', account],
    queryFn: () => apiClient.get('/api/v1/procurement/summary', { params: { account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const drillQueryFn = useCallback(
    () => apiClient.get('/api/sankey/drilldown', {
      params: {
        nodeType: 'vendor',
        nodeValue: drillVendor,
        department: departmentId,
        limit: 200,
        offset: 0,
      },
    }),
    [drillVendor, departmentId],
  );

  if (isLoading) return <div className="accounts-panel-note">Loading competitive footprint…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load competitive footprint'}
      </div>
    );
  }

  const summary = data?.summary || {};
  const ourFootprint = data?.ourFootprint || [];
  const competitorFootprint = data?.competitorFootprint || [];
  const total = Number(summary.totalAddressable) || 0;
  const spendTrend = trendQuery.data?.spendTrend || [];
  // Same tolerant key handling the Dash summary uses — this endpoint has
  // shipped both camelCase and snake_case shapes.
  const categories = (
    categoryQuery.data?.topCategories || categoryQuery.data?.top_categories || []
  ).slice(0, 8);

  if (ourFootprint.length === 0 && competitorFootprint.length === 0) {
    return (
      <div className="ts-card">
        <p className="accounts-panel-note">
          No procurement footprint on record for this agency in the last {FOOTPRINT_YEARS} years.
        </p>
      </div>
    );
  }

  return (
    <div className="accounts-competitive">
      <div className="ts-stat-row">
        <div className="ts-stat-card">
          <div className="ts-stat-card__value">{summary.ourSpendFormatted || fmtMoney(summary.ourSpend)}</div>
          <div className="ts-stat-card__label">Our spend ({FOOTPRINT_YEARS}-yr)</div>
        </div>
        <div className="ts-stat-card">
          <div className="ts-stat-card__value">{summary.competitorSpendFormatted || fmtMoney(summary.competitorSpend)}</div>
          <div className="ts-stat-card__label">Competitor spend · {summary.competitorVendorCount ?? competitorFootprint.length} vendors</div>
        </div>
        <div className="ts-stat-card">
          <div className="ts-stat-card__value">{summary.totalAddressableFormatted || fmtMoney(total)}</div>
          <div className="ts-stat-card__label">Total addressable</div>
        </div>
        <div className="ts-stat-card">
          <div className="ts-stat-card__value">{summary.ourShare || '0%'}</div>
          <div className="ts-stat-card__label">Our share</div>
        </div>
      </div>

      <div className="ts-card">
        <h3 className="ts-card-title">Spend trend</h3>
        {trendQuery.isLoading && <p className="accounts-panel-note">Loading spend trend…</p>}
        {trendQuery.error && (
          <p className="cmp-trend-error">
            Couldn't load the spend trend — {trendQuery.error.message || 'request failed'}.
          </p>
        )}
        {!trendQuery.isLoading && !trendQuery.error && (
          spendTrend.length > 0
            ? (
              <div className="cmp-trend">
                <SpendTrendChart data={spendTrend} height={180} />
                <p className="cmp-note">All-vendor spend by fiscal year (5-yr window).</p>
              </div>
            )
            : <p className="accounts-panel-note">No spend history recorded for this agency.</p>
        )}
      </div>

      {categories.length > 0 && (
        <div className="ts-card">
          <h3 className="ts-card-title">Spend by category</h3>
          <CategoryRows categories={categories} />
        </div>
      )}

      {ourFootprint.length > 0 && (
        <div className="ts-card">
          <div className="accounts-card-head">
            <h3 className="ts-card-title">Our footprint</h3>
            <span className="cmp-note">click a vendor for transactions</span>
          </div>
          <VendorRows vendors={ourFootprint} total={total} ours onDrill={setDrillVendor} />
        </div>
      )}

      <div className="ts-card">
        <div className="accounts-card-head">
          <h3 className="ts-card-title">Competitor footprint</h3>
          <span className="cmp-note">top {competitorFootprint.length} by {FOOTPRINT_YEARS}-yr spend · click a vendor for transactions</span>
        </div>
        {competitorFootprint.length === 0
          ? <p className="accounts-panel-note">No competitor spend recorded in this window.</p>
          : <VendorRows vendors={competitorFootprint} total={total} onDrill={setDrillVendor} />}
      </div>

      <SharedTransactionsDrilldownModal
        isOpen={!!drillVendor}
        onClose={() => setDrillVendor(null)}
        title={drillVendor || ''}
        contextLabel="Vendor transactions"
        vendor={drillVendor || undefined}
        departmentId={departmentId}
        chips={[
          { icon: <Building2 size={12} />, label: selectedDepartment?.acronym || `Dept ${departmentId}` },
        ]}
        queryKey={['accounts-competitive-drill', departmentId, drillVendor]}
        queryFn={drillQueryFn}
      />
    </div>
  );
}
